import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Upload, FileText, PenLine, Send, ArrowLeft, Check, Copy, Mail, Shield, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '@/shared/contexts/AuthContext'
import {
  createEnvelope,
  addRecipient,
  updateEnvelopeStatus,
} from '../services/envelopeService'
import { getStoredSignatures, type StoredSignature } from '@/features/my-signatures/services/signatureStorage'
import {
  embedSignatureInPdf,
  fileToUint8Array,
  uint8ArrayToBase64,
  createPdfBlobUrl,
} from '@/shared/lib/pdf-utils'
import { computeFileSHA256 } from '@/shared/lib/crypto'
import { apiClient } from '@/shared/api/client'
import toast from 'react-hot-toast'
import { useEffect } from 'react'

type Step = 'setup' | 'upload' | 'sign' | 'recipient' | 'done'
type SigningMethod = 'electronic' | 'pkcs12'

const COUNTRIES = [
  { code: 'AR', label: 'Argentina', flag: '🇦🇷', law: 'Ley 25.506' },
  { code: 'EC', label: 'Ecuador', flag: '🇪🇨', law: 'Ley 67 / Ley de Comercio Electrónico' },
  { code: 'CL', label: 'Chile', flag: '🇨🇱', law: 'Ley 19.799' },
  { code: 'CO', label: 'Colombia', flag: '🇨🇴', law: 'Ley 527' },
]

export function CreateEnvelope() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [step, setStep] = useState<Step>('setup')
  const [country, setCountry] = useState('AR')
  const [signingMethod, setSigningMethod] = useState<SigningMethod>('electronic')
  const [file, setFile] = useState<File | null>(null)
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null)
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null)
  const [signatures, setSignatures] = useState<StoredSignature[]>([])
  const [selectedSignature, setSelectedSignature] = useState<StoredSignature | null>(null)
  const [signedPdfBytes, setSignedPdfBytes] = useState<Uint8Array | null>(null)
  const [title, setTitle] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [message, setMessage] = useState('')
  const [signingLink, setSigningLink] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [inviteMessage, setInviteMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [signing, setSigning] = useState(false)
  // PKI signing state (for sender when method=pkcs12)
  const [p12File, setP12File] = useState<File | null>(null)
  const [p12Password, setP12Password] = useState('')
  const [showP12Password, setShowP12Password] = useState(false)
  const p12InputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    getStoredSignatures().then(setSignatures).catch(console.error)
  }, [])

  // Step 1: Upload PDF
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF')
      return
    }
    if (f.size > 3 * 1024 * 1024) {
      toast.error('El archivo no debe superar 3MB')
      return
    }
    setFile(f)
    setTitle(f.name.replace(/\.pdf$/i, ''))
    const bytes = await fileToUint8Array(f)
    setPdfBytes(bytes)
    const url = createPdfBlobUrl(uint8ArrayToBase64(bytes))
    setPdfPreviewUrl(url)
    setStep('sign')
  }, [])

  const handleFileDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    const f = e.dataTransfer.files[0]
    if (!f || f.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF')
      return
    }
    if (f.size > 3 * 1024 * 1024) {
      toast.error('El archivo no debe superar 3MB')
      return
    }
    setFile(f)
    setTitle(f.name.replace(/\.pdf$/i, ''))
    const bytes = await fileToUint8Array(f)
    setPdfBytes(bytes)
    const url = createPdfBlobUrl(uint8ArrayToBase64(bytes))
    setPdfPreviewUrl(url)
    setStep('sign')
  }, [])

  // Step 2: Sign the document (electronic mode — drawn/uploaded signature)
  const handleSign = async () => {
    if (!pdfBytes || !selectedSignature || !user) return
    setSigning(true)
    try {
      const displayName = user.user_metadata?.full_name || user.user_metadata?.display_name || user.email || 'Remitente'
      const signed = await embedSignatureInPdf(
        pdfBytes,
        selectedSignature.image_data,
        displayName,
        'left'
      )
      setSignedPdfBytes(signed)
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl)
      const url = createPdfBlobUrl(uint8ArrayToBase64(signed))
      setPdfPreviewUrl(url)
      toast.success('Documento firmado')
      setStep('recipient')
    } catch (err) {
      console.error('Error signing:', err)
      toast.error('Error al firmar el documento')
    } finally {
      setSigning(false)
    }
  }

  // Step 2 (PKI mode): Sign with PKCS#12 certificate via backend
  const handlePkiSign = async () => {
    if (!file || !p12File || !p12Password || !user) return
    setSigning(true)
    try {
      const formData = new FormData()
      formData.append('pdf', file)
      formData.append('certificate', p12File)
      formData.append('password', p12Password)
      const displayName = user.user_metadata?.full_name || user.user_metadata?.display_name || user.email || 'Remitente'
      formData.append('signer_name', displayName)
      formData.append('reason', `Firma de ${title || file.name}`)
      formData.append('location', COUNTRIES.find(c => c.code === country)?.label ?? country)

      const response = await apiClient.post('/api/sign-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
        responseType: 'json',
      })

      const signedB64: string = response.data?.signed_pdf_base64 ?? ''
      if (!signedB64) throw new Error('No se recibió el PDF firmado')

      // Decode base64 → Uint8Array for storage
      const signedBytes = Uint8Array.from(atob(signedB64), c => c.charCodeAt(0))
      setSignedPdfBytes(signedBytes)
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl)
      setPdfPreviewUrl(createPdfBlobUrl(signedB64))
      setP12Password('')
      toast.success('Documento firmado con certificado digital')
      setStep('recipient')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al firmar'
      toast.error(msg)
    } finally {
      setSigning(false)
    }
  }

  // Step 3: Add recipient and send
  const handleSend = async () => {
    if (!user || !file || !signedPdfBytes) return
    if (!recipientEmail || !recipientName) {
      toast.error('Completa el nombre y email del destinatario')
      return
    }

    setSending(true)
    try {
      const hash = await computeFileSHA256(file)
      const docBase64 = uint8ArrayToBase64(signedPdfBytes)

      // Create envelope
      const envelope = await createEnvelope({
        senderId: user.id,
        title: title || file.name,
        message: message || undefined,
        filename: file.name,
        documentData: docBase64,
        hash,
        country,
        signingMethod,
      })

      // Add recipient
      const recipient = await addRecipient({
        envelopeId: envelope.id,
        email: recipientEmail,
        name: recipientName,
      })

      // Mark as sent
      await updateEnvelopeStatus(envelope.id, 'sent')

      // Generate signing link
      const link = `${window.location.origin}/firmar-invitacion/${recipient.access_token}`
      setSigningLink(link)

      // Send invitation email automátically via backend
      try {
        const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipient_email: recipientEmail,
            recipient_name: recipientName,
            signing_url: link,
          }),
        })
        const result = await resp.json()
        setEmailSent(result.sent)
        setInviteMessage(result.message)
        if (result.sent) {
          toast.success(`Email enviado a ${recipientEmail}`)
        }
      } catch {
        // Email failed but envelope was created successfully
        setEmailSent(false)
        setInviteMessage('No se pudo enviar el email automáticamente')
      }

      setStep('done')
      toast.success('Documento enviado para firma')
    } catch (err) {
      console.error('Error sending:', err)
      toast.error('Error al enviar el documento')
    } finally {
      setSending(false)
    }
  }

  const copyLink = () => {
    navigator.clipboard.writeText(signingLink)
    toast.success('Link copiado al portapapeles')
  }

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/documentos')}
        className="flex items-center gap-2 text-sm text-legal-500 dark:text-legal-400 hover:text-legal-700 dark:hover:text-legal-200 mb-4"
      >
        <ArrowLeft size={16} />
        Volver a Documentos
      </button>

      <h1 className="text-2xl font-bold text-legal-900 dark:text-white mb-6">
        Nuevo Documento para Firma
      </h1>

      {/* Progress steps */}
      <div className="flex items-center gap-2 mb-8">
        {(['setup', 'upload', 'sign', 'recipient', 'done'] as Step[]).map((s, i) => {
          const labels = ['Configurar', 'Subir PDF', 'Firmar', 'Destinatario', 'Enviado']
          const stepOrder: Step[] = ['setup', 'upload', 'sign', 'recipient', 'done']
          const currentIdx = stepOrder.indexOf(step)
          const thisIdx = i
          const isActive = thisIdx === currentIdx
          const isDone = thisIdx < currentIdx

          return (
            <div key={s} className="flex items-center gap-2">
              {i > 0 && <div className={`w-8 h-0.5 ${isDone || isActive ? 'bg-primary-500' : 'bg-legal-300 dark:bg-legal-600'}`} />}
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                isActive
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : isDone
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-legal-100 dark:bg-legal-700 text-legal-500 dark:text-legal-400'
              }`}>
                {isDone ? <Check size={12} /> : <span>{i + 1}</span>}
                <span className="hidden sm:inline">{labels[i]}</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Step 0: Setup — country + signing method */}
      {step === 'setup' && (
        <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-3">
              Jurisdicción del documento
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              {COUNTRIES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => setCountry(c.code)}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    country === c.code
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-legal-200 dark:border-legal-700 hover:border-legal-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">{c.flag}</span>
                    <span className="font-medium text-sm text-legal-900 dark:text-white">{c.label}</span>
                  </div>
                  <p className="text-xs text-legal-500 dark:text-legal-400">{c.law}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-3">
              Método de firma
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSigningMethod('electronic')}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                  signingMethod === 'electronic'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-legal-200 dark:border-legal-700 hover:border-legal-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <PenLine size={18} className="text-legal-600 dark:text-legal-300" />
                  <span className="font-medium text-sm text-legal-900 dark:text-white">Firma Electrónica</span>
                </div>
                <p className="text-xs text-legal-500 dark:text-legal-400">Imagen de firma dibujada o cargada</p>
              </button>
              <button
                type="button"
                onClick={() => setSigningMethod('pkcs12')}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                  signingMethod === 'pkcs12'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-legal-200 dark:border-legal-700 hover:border-legal-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Shield size={18} className="text-primary-600 dark:text-primary-400" />
                  <span className="font-medium text-sm text-legal-900 dark:text-white">Firma Digital PKI</span>
                </div>
                <p className="text-xs text-legal-500 dark:text-legal-400">Certificado .p12 emitido por CA acreditada</p>
              </button>
            </div>
          </div>

          {signingMethod === 'pkcs12' && (
            <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <Shield size={16} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Ambas partes deberán tener su certificado .p12 y contraseña listos para firmar.
                El certificado se usa solo para la firma y nunca se almacena.
              </p>
            </div>
          )}

          <button
            onClick={() => setStep('upload')}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            Continuar
          </button>
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleFileDrop}
          className="border-2 border-dashed border-legal-300 dark:border-legal-600 rounded-xl p-12 text-center hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
        >
          <Upload size={48} className="mx-auto text-legal-400 dark:text-legal-500 mb-4" />
          <p className="text-lg text-legal-700 dark:text-legal-200 mb-2">
            Arrastra tu documento PDF aquí
          </p>
          <p className="text-sm text-legal-500 dark:text-legal-400 mb-4">
            o hace click para seleccionar (max 3MB)
          </p>
          <label className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer text-sm font-medium">
            <FileText size={18} />
            Seleccionar PDF
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        </div>
      )}

      {/* Step 2: Sign */}
      {step === 'sign' && (
        <div className="space-y-6">
          {/* PDF Preview */}
          {pdfPreviewUrl && (
            <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-legal-200 dark:border-legal-700">
                <p className="text-sm font-medium text-legal-700 dark:text-legal-200">
                  <FileText size={16} className="inline mr-2" />
                  {file?.name}
                </p>
              </div>
              <iframe
                src={pdfPreviewUrl}
                className="w-full h-[220px] sm:h-[340px] md:h-[400px] border-0"
                title="Vista previa del PDF"
              />
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-1">
              Titulo del documento
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-900 text-legal-900 dark:text-white text-sm"
              placeholder="Ej: Contrato de Servicios"
            />
          </div>

          {signingMethod === 'pkcs12' ? (
            /* PKI signing mode */
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Shield size={16} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Tu certificado se usa solo para esta firma y no se almacena.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-1">
                  Certificado digital (.p12 / .pfx)
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => p12InputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 border border-legal-300 dark:border-legal-600 rounded-lg text-sm text-legal-700 dark:text-legal-200 hover:bg-legal-50 dark:hover:bg-legal-700"
                  >
                    <Upload size={16} />
                    {p12File ? p12File.name : 'Seleccionar .p12'}
                  </button>
                  {p12File && (
                    <span className="text-xs text-legal-500">{(p12File.size / 1024).toFixed(1)} KB</span>
                  )}
                </div>
                <input
                  ref={p12InputRef}
                  type="file"
                  accept=".p12,.pfx,application/x-pkcs12"
                  onChange={(e) => setP12File(e.target.files?.[0] ?? null)}
                  className="hidden"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-1">
                  Contraseña del certificado
                </label>
                <div className="relative max-w-sm">
                  <input
                    type={showP12Password ? 'text' : 'password'}
                    value={p12Password}
                    onChange={(e) => setP12Password(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Contraseña del .p12"
                    className="w-full pr-10 px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-900 text-legal-900 dark:text-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowP12Password(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-legal-400 hover:text-legal-600"
                  >
                    {showP12Password ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => { setStep('upload'); setFile(null); setPdfBytes(null) }}
                  className="px-4 py-2 text-sm border border-legal-300 dark:border-legal-600 text-legal-600 dark:text-legal-300 rounded-lg hover:bg-legal-50 dark:hover:bg-legal-700"
                >
                  Cambiar PDF
                </button>
                <button
                  onClick={handlePkiSign}
                  disabled={!p12File || !p12Password || signing}
                  className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Shield size={16} />
                  {signing ? 'Firmando...' : 'Firmar con Certificado'}
                </button>
              </div>
            </div>
          ) : (
            /* Electronic signing mode — drawn/uploaded signature */
            <>
              <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-2">
                Selecciona tu firma
              </label>
              {signatures.length === 0 ? (
                <div className="text-center py-6 bg-legal-50 dark:bg-legal-900 rounded-lg border border-legal-200 dark:border-legal-700">
                  <PenLine size={24} className="mx-auto text-legal-400 mb-2" />
                  <p className="text-sm text-legal-500 dark:text-legal-400 mb-3">
                    Necesitas crear una firma primero
                  </p>
                  <button
                    onClick={() => navigate('/mis-firmas')}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
                  >
                    Ir a Mis Firmas
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {signatures.map((sig) => (
                    <button
                      key={sig.id}
                      onClick={() => setSelectedSignature(sig)}
                      className={`p-3 rounded-lg border-2 transition-colors text-left ${
                        selectedSignature?.id === sig.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-legal-200 dark:border-legal-700 hover:border-legal-300 dark:hover:border-legal-600'
                      }`}
                    >
                      <img
                        src={sig.image_data}
                        alt={sig.name}
                        className="h-12 object-contain mb-1"
                      />
                      <p className="text-xs text-legal-500 dark:text-legal-400">{sig.name}</p>
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => { setStep('upload'); setFile(null); setPdfBytes(null) }}
                  className="px-4 py-2 text-sm border border-legal-300 dark:border-legal-600 text-legal-600 dark:text-legal-300 rounded-lg hover:bg-legal-50 dark:hover:bg-legal-700"
                >
                  Cambiar PDF
                </button>
                <button
                  onClick={handleSign}
                  disabled={!selectedSignature || signing}
                  className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PenLine size={16} />
                  {signing ? 'Firmando...' : 'Firmar Documento'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 3: Recipient */}
      {step === 'recipient' && (
        <div className="space-y-6">
          {pdfPreviewUrl && (
            <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-legal-200 dark:border-legal-700 flex items-center gap-2">
                <Check size={16} className="text-green-600" />
                <p className="text-sm font-medium text-green-700 dark:text-green-300">
                  Documento firmado - vista previa
                </p>
              </div>
              <iframe
                src={pdfPreviewUrl}
                className="w-full h-[200px] sm:h-[300px] border-0"
                title="PDF firmado"
              />
            </div>
          )}

          <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 p-6">
            <h3 className="text-lg font-semibold text-legal-900 dark:text-white mb-4">
              Destinatario
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-1">
                  Nombre completo
                </label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  className="w-full px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-900 text-legal-900 dark:text-white text-sm"
                  placeholder="Juan Perez"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-900 text-legal-900 dark:text-white text-sm"
                  placeholder="juan@ejemplo.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-1">
                Mensaje (opcional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-900 text-legal-900 dark:text-white text-sm resize-none"
                placeholder="Mensaje para el firmante..."
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStep('sign')}
              className="px-4 py-2 text-sm border border-legal-300 dark:border-legal-600 text-legal-600 dark:text-legal-300 rounded-lg hover:bg-legal-50 dark:hover:bg-legal-700"
            >
              Volver
            </button>
            <button
              onClick={handleSend}
              disabled={!recipientEmail || !recipientName || sending}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={16} />
              {sending ? 'Enviando...' : 'Enviar para Firma'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Done */}
      {step === 'done' && (
        <div className="text-center py-8 bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 p-8">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            {emailSent ? (
              <Mail size={32} className="text-green-600 dark:text-green-400" />
            ) : (
              <Check size={32} className="text-green-600 dark:text-green-400" />
            )}
          </div>
          <h2 className="text-xl font-bold text-legal-900 dark:text-white mb-2">
            {emailSent ? 'Invitacion enviada por email' : 'Documento listo para firma'}
          </h2>

          {emailSent ? (
            <p className="text-sm text-legal-500 dark:text-legal-400 mb-6">
              Se envio un email a <strong>{recipientName}</strong> ({recipientEmail}) con un link para revisar y firmar el documento.
            </p>
          ) : (
            <p className="text-sm text-legal-500 dark:text-legal-400 mb-2">
              {inviteMessage || 'Comparte el link con el destinatario.'}
            </p>
          )}

          {/* Always show the link as fallback / reference */}
          <div className="bg-legal-50 dark:bg-legal-900 rounded-lg p-4 mb-6 max-w-lg mx-auto">
            <p className="text-xs text-legal-500 dark:text-legal-400 mb-2">
              {emailSent ? 'Link de referencia (también enviado por email):' : 'Link de firma:'}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs text-legal-700 dark:text-legal-200 break-all bg-white dark:bg-legal-800 px-3 py-2 rounded border border-legal-200 dark:border-legal-700">
                {signingLink}
              </code>
              <button
                onClick={copyLink}
                className="flex-shrink-0 p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                title="Copiar link"
              >
                <Copy size={16} />
              </button>
            </div>
          </div>

          <button
            onClick={() => navigate('/documentos')}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            Ir a Mis Documentos
          </button>
        </div>
      )}
    </div>
  )
}
